import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  MessageSquare,
  Search,
  Zap,
  Cpu,
  Shield,
  ArrowRight,
  Check,
  Plug,
  Globe,
  BarChart3,
  Blocks,
  SlidersHorizontal,
  Ticket,
  ShoppingCart,
  Wallet,
  CalendarCheck,
  FileText,
  Heart,
  Store,
  BookOpen,
  Briefcase,
  Landmark,
  MapPin,
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTelegram, faKoFi } from '@fortawesome/free-brands-svg-icons';
import { faMessage, faCalendar, faMugHot, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { Button, FeatureIcon, Badge } from '@/components/ui';
import { Card } from '@/components/ui/Card';
import { SectionDivider } from '@/components/decorative';

const features = [
  {
    icon: MessageSquare,
    title: 'Conversation Replaces UI',
    description: 'Customers say what they need. AI handles it.',
    benefit: 'Zero learning curve',
    gradient: 'from-indigo-500/20 to-purple-500/20',
    iconColor: 'text-indigo-400',
  },
  {
    icon: Search,
    title: 'Context-Aware',
    description: 'Full conversation memory. No repeating yourself.',
    benefit: 'Smart context',
    gradient: 'from-blue-500/20 to-cyan-500/20',
    iconColor: 'text-blue-400',
  },
  {
    icon: Plug,
    title: 'MCP Endpoint',
    description: 'AI agents discover and transact with your business directly.',
    benefit: 'Coming soon',
    gradient: 'from-purple-500/20 to-pink-500/20',
    iconColor: 'text-purple-400',
    comingSoon: true,
  },
  {
    icon: Zap,
    title: 'Action Cards',
    description: 'Every action as a visual card — confirm, approve, review in one tap.',
    benefit: 'Clear actions',
    gradient: 'from-emerald-500/20 to-teal-500/20',
    iconColor: 'text-emerald-400',
  },
  {
    icon: Cpu,
    title: 'Built for Scale',
    description: 'Thousands of concurrent conversations. Sub-second responses.',
    benefit: 'Enterprise-ready',
    gradient: 'from-orange-500/20 to-red-500/20',
    iconColor: 'text-orange-400',
  },
  {
    icon: Globe,
    title: 'Beyond Search Engines',
    description: 'MCP lets AI agents reach your service — no search engine needed.',
    benefit: 'Coming soon',
    gradient: 'from-amber-500/20 to-yellow-500/20',
    iconColor: 'text-amber-400',
    comingSoon: true,
  },
  {
    icon: SlidersHorizontal,
    title: 'The DialogMe Harness',
    description:
      'Your vertical\u2019s workflows, gates, policies and evaluation sets \u2014 configured, versioned and maintained.',
    benefit: 'Included in every plan',
    gradient: 'from-amber-500/20 to-orange-500/20',
    iconColor: 'text-amber-400',
  },
  {
    icon: Blocks,
    title: 'Bring Your Own Model',
    description: 'Connect your own approved model endpoint. Pay only for service usage.',
    benefit: 'Coming soon',
    gradient: 'from-rose-500/20 to-orange-500/20',
    iconColor: 'text-rose-400',
    comingSoon: true,
  },
  {
    icon: BarChart3,
    title: 'Advanced Analytics',
    description: 'Deep insights into conversations, usage patterns, and service performance.',
    benefit: 'Coming soon',
    gradient: 'from-teal-500/20 to-cyan-500/20',
    iconColor: 'text-teal-400',
    comingSoon: true,
  },
];

interface Provider {
  name: string;
  ready?: boolean;
}

interface Integration {
  name: string;
  key: string;
  icon: React.ReactNode;
  comingSoon?: boolean;
  providers: Provider[];
}

const integrations: Integration[] = [
  {
    name: 'Chat', key: 'chat', icon: <MessageSquare className="w-4 h-4" />,
    providers: [
      { name: 'Telegram', ready: true },
      { name: 'Email', ready: true },
    ],
  },
  {
    name: 'Ticket', key: 'ticket', icon: <Ticket className="w-4 h-4" />,
    providers: [
      { name: 'DialogMe', ready: true },
    ],
  },
  {
    name: 'Shop', key: 'shop', icon: <ShoppingCart className="w-4 h-4" />, comingSoon: true,
    providers: [],
  },
  {
    name: 'Payment', key: 'payment', icon: <Wallet className="w-4 h-4" />, comingSoon: true,
    providers: [],
  },
  {
    name: 'Appointment', key: 'appointment', icon: <CalendarCheck className="w-4 h-4" />,
    providers: [
      { name: 'Cal.com', ready: true },
    ],
  },
  {
    name: 'Post', key: 'post', icon: <FileText className="w-4 h-4" />,
    providers: [
      { name: 'DialogMe', ready: true },
    ],
  },
  {
    name: 'Subscription', key: 'subscription', icon: <Heart className="w-4 h-4" />,
    providers: [
      { name: 'Ko-fi', ready: true },
      { name: 'Buy Me a Coffee', ready: true },
    ],
  },
  {
    name: 'Inference', key: 'inference', icon: <Cpu className="w-4 h-4" />,
    providers: [
      { name: 'DialogMe', ready: true },
    ],
  },
];

const providerIcons: Record<string, React.ReactNode> = {
  'Telegram': <FontAwesomeIcon icon={faTelegram} className="w-3.5 h-3.5" />,
  'Email': <FontAwesomeIcon icon={faEnvelope} className="w-3.5 h-3.5" />,
  'DialogMe': <FontAwesomeIcon icon={faMessage} className="w-3.5 h-3.5" />,
  'Cal.com': <FontAwesomeIcon icon={faCalendar} className="w-3.5 h-3.5" />,
  'Ko-fi': <FontAwesomeIcon icon={faKoFi} className="w-3.5 h-3.5" />,
  'Buy Me a Coffee': <FontAwesomeIcon icon={faMugHot} className="w-3.5 h-3.5" />,
};

interface ServiceType {
  name: string;
  key: string;
  icon: React.ReactNode;
  gradient: string;
  iconColor: string;
  integrationKeys: string[];
}

const serviceTypes: ServiceType[] = [
  { name: 'E-Commerce', key: 'e-commerce', icon: <Store className="w-5 h-5" />, gradient: 'from-emerald-500/20 to-teal-500/20', iconColor: 'text-emerald-400', integrationKeys: ['chat', 'ticket', 'shop', 'payment'] },
  { name: 'Blog', key: 'blog', icon: <BookOpen className="w-5 h-5" />, gradient: 'from-blue-500/20 to-cyan-500/20', iconColor: 'text-blue-400', integrationKeys: ['chat', 'post', 'subscription'] },
  { name: 'Portfolio', key: 'portfolio', icon: <Briefcase className="w-5 h-5" />, gradient: 'from-indigo-500/20 to-purple-500/20', iconColor: 'text-indigo-400', integrationKeys: ['chat', 'post'] },
  { name: 'Finance', key: 'finance', icon: <Landmark className="w-5 h-5" />, gradient: 'from-amber-500/20 to-orange-500/20', iconColor: 'text-amber-400', integrationKeys: ['chat', 'ticket'] },
  { name: 'Offline Business', key: 'offline_business', icon: <MapPin className="w-5 h-5" />, gradient: 'from-rose-500/20 to-pink-500/20', iconColor: 'text-rose-400', integrationKeys: ['chat', 'appointment', 'post'] },
];

type HoverTarget = { type: 'service'; key: string } | { type: 'integration'; key: string } | null;

interface PathData {
  d: string;
  serviceKey?: string;
  integrationKey?: string;
}

export function FeaturesPage() {
  const [hover, setHover] = useState<HoverTarget>(null);
  const graphRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef(new Map<string, HTMLDivElement>());
  const [paths, setPaths] = useState<PathData[]>([]);

  const setNodeRef = useCallback((key: string) => (el: HTMLDivElement | null) => {
    if (el) nodeRefs.current.set(key, el);
    else nodeRefs.current.delete(key);
  }, []);

  const recalcPaths = useCallback(() => {
    const container = graphRef.current;
    if (!container) return;
    const cr = container.getBoundingClientRect();
    const next: PathData[] = [];

    for (const st of serviceTypes) {
      const stEl = nodeRefs.current.get(`st-${st.key}`);
      if (!stEl) continue;
      const sr = stEl.getBoundingClientRect();
      const fromX = sr.right - cr.left;
      const fromY = sr.top + sr.height / 2 - cr.top;

      for (const ik of st.integrationKeys) {
        const intEl = nodeRefs.current.get(`int-${ik}`);
        if (!intEl) continue;
        const ir = intEl.getBoundingClientRect();
        const toX = ir.left - cr.left;
        const toY = ir.top + ir.height / 2 - cr.top;
        const cp = Math.min(Math.abs(toX - fromX) * 0.45, 70);
        next.push({
          d: `M${fromX},${fromY} C${fromX + cp},${fromY} ${toX - cp},${toY} ${toX},${toY}`,
          serviceKey: st.key,
          integrationKey: ik,
        });
      }
    }

    for (const intg of integrations) {
      const intEl = nodeRefs.current.get(`int-${intg.key}`);
      const rowEl = nodeRefs.current.get(`provrow-${intg.key}`);
      if (!intEl || !rowEl) continue;
      const ir = intEl.getBoundingClientRect();
      const rr = rowEl.getBoundingClientRect();
      const fromX = ir.right - cr.left;
      const fromY = ir.top + ir.height / 2 - cr.top;
      const toX = rr.left - cr.left;
      const toY = rr.top + rr.height / 2 - cr.top;
      const cp = Math.min(Math.abs(toX - fromX) * 0.45, 70);
      next.push({
        d: `M${fromX},${fromY} C${fromX + cp},${fromY} ${toX - cp},${toY} ${toX},${toY}`,
        integrationKey: intg.key,
      });
    }

    setPaths(next);
  }, []);

  useEffect(() => {
    recalcPaths();
    const ro = new ResizeObserver(recalcPaths);
    if (graphRef.current) ro.observe(graphRef.current);
    window.addEventListener('resize', recalcPaths);
    return () => { ro.disconnect(); window.removeEventListener('resize', recalcPaths); };
  }, [recalcPaths]);

  // Compute active sets based on hover
  const activeServices = new Set<string>();
  const activeIntegrations = new Set<string>();

  if (hover?.type === 'service') {
    const st = serviceTypes.find(s => s.key === hover.key);
    if (st) {
      activeServices.add(st.key);
      st.integrationKeys.forEach(ik => activeIntegrations.add(ik));
    }
  } else if (hover?.type === 'integration') {
    activeIntegrations.add(hover.key);
    for (const st of serviceTypes) {
      if (st.integrationKeys.includes(hover.key)) activeServices.add(st.key);
    }
  }

  const isPathActive = (p: PathData) => {
    if (!hover) return false;
    if (p.serviceKey && p.integrationKey) {
      return activeServices.has(p.serviceKey) && activeIntegrations.has(p.integrationKey);
    }
    if (p.integrationKey && !p.serviceKey) {
      return activeIntegrations.has(p.integrationKey);
    }
    return false;
  };

  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-20 pb-10 md:py-20">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 rounded-full mb-6 border border-indigo-500/20">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">Features</span>
          </div>
          <h1 className="text-hero-mobile md:text-hero-desktop font-extrabold tracking-heading mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
            One Platform. Every Integration.
          </h1>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Pick your industry. We handle the rest.
          </p>
        </div>

        {/* Service Types & Integrations */}
        <div className="my-12 md:my-16">
          <div className="text-center mb-8 sm:mb-10">
            <Badge variant="premium" size="md" className="mb-4">
              Built for Your Industry
            </Badge>
            <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3">
              Your Industry. Your Integrations.
            </h2>
            <p className="text-sm text-slate-400 max-w-xl mx-auto leading-relaxed">
              Choose your business type. Get the right tools instantly.
            </p>
          </div>

          {/* Desktop: Interactive dependency graph */}
          <div className="hidden md:block relative" ref={graphRef}>
            {/* SVG connection lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
              {paths.map((p, i) => {
                const active = isPathActive(p);
                return (
                  <path
                    key={i}
                    d={p.d}
                    fill="none"
                    stroke={active ? '#818cf8' : '#475569'}
                    strokeWidth={active ? 1.5 : 1}
                    opacity={hover ? (active ? 0.9 : 0.08) : 0.25}
                    className="transition-all duration-200"
                  />
                );
              })}
            </svg>

            {/* Column headers */}
            <div className="flex gap-8 lg:gap-12 mb-4 pl-1">
              <div className="w-36 lg:w-44 flex-shrink-0 text-[11px] font-semibold text-slate-500 uppercase tracking-widest">
                Business Types
              </div>
              <div className="flex-1 flex gap-8 lg:gap-12">
                <div className="w-32 lg:w-40 flex-shrink-0 text-[11px] font-semibold text-slate-500 uppercase tracking-widest">
                  Service Types
                </div>
                <div className="flex-1 text-[11px] font-semibold text-slate-500 uppercase tracking-widest">
                  Providers
                </div>
              </div>
            </div>

            {/* Graph content */}
            <div className="relative flex gap-8 lg:gap-12">
              {/* Column 1: Service Types */}
              <div className="w-36 lg:w-44 flex-shrink-0 flex flex-col justify-evenly py-1 gap-1.5">
                {serviceTypes.map((st) => {
                  const lit = !hover || activeServices.has(st.key);
                  return (
                    <div
                      key={st.key}
                      ref={setNodeRef(`st-${st.key}`)}
                      onMouseEnter={() => setHover({ type: 'service', key: st.key })}
                      onMouseLeave={() => setHover(null)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 border ${
                        hover && activeServices.has(st.key)
                          ? 'bg-indigo-500/10 border-indigo-400/30 shadow-[0_0_12px_rgba(99,102,241,0.15)]'
                          : hover && !lit
                            ? 'bg-slate-800/20 border-slate-800/30'
                            : 'bg-slate-800/40 border-slate-700/40 hover:border-slate-600/60'
                      }`}
                    >
                      <span className={`flex-shrink-0 transition-colors duration-200 ${hover && !lit ? 'text-slate-700' : st.iconColor}`}>
                        {st.icon}
                      </span>
                      <span className={`text-xs font-medium truncate transition-colors duration-200 ${
                        hover && activeServices.has(st.key) ? 'text-white' : hover && !lit ? 'text-slate-600' : 'text-slate-300'
                      }`}>
                        {st.name}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Columns 2+3: Integration rows with providers */}
              <div className="flex-1 space-y-2">
                {integrations.map((intg) => {
                  const lit = !hover || activeIntegrations.has(intg.key);
                  return (
                    <div key={intg.key} className="flex items-center gap-8 lg:gap-12">
                      {/* Integration node */}
                      <div
                        ref={setNodeRef(`int-${intg.key}`)}
                        onMouseEnter={() => setHover({ type: 'integration', key: intg.key })}
                        onMouseLeave={() => setHover(null)}
                        className={`w-32 lg:w-40 flex-shrink-0 flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 border ${
                          hover && activeIntegrations.has(intg.key)
                            ? 'bg-indigo-500/10 border-indigo-400/30 shadow-[0_0_12px_rgba(99,102,241,0.15)]'
                            : hover && !lit
                              ? 'bg-slate-800/20 border-slate-800/30'
                              : 'bg-slate-800/40 border-slate-700/40 hover:border-slate-600/60'
                        }`}
                      >
                        <span className={`flex-shrink-0 transition-colors duration-200 ${
                          hover && activeIntegrations.has(intg.key) ? 'text-indigo-400' : hover && !lit ? 'text-slate-700' : 'text-slate-400'
                        }`}>
                          {intg.icon}
                        </span>
                        <span className={`text-xs font-medium truncate transition-colors duration-200 ${
                          hover && activeIntegrations.has(intg.key) ? 'text-white' : hover && !lit ? 'text-slate-600' : 'text-slate-300'
                        }`}>
                          {intg.name}
                        </span>
                        {intg.comingSoon && (
                          <span className={`text-[10px] ml-auto flex-shrink-0 transition-colors duration-200 ${
                            hover && activeIntegrations.has(intg.key) ? 'text-amber-300' : hover && !lit ? 'text-slate-700' : 'text-amber-400/70'
                          }`}>
                            soon
                          </span>
                        )}
                      </div>

                      {/* Provider row */}
                      <div ref={setNodeRef(`provrow-${intg.key}`)} className={`flex-1 flex flex-wrap gap-1.5 px-3 py-2 rounded-xl transition-all duration-200 border ${
                        hover && activeIntegrations.has(intg.key)
                          ? 'bg-indigo-500/[0.07] border-indigo-400/20'
                          : hover && !lit
                            ? 'bg-transparent border-transparent'
                            : 'bg-slate-800/20 border-slate-700/20'
                      }`}>
                        {intg.providers.length > 0 ? (
                          <>
                          {intg.providers.map((prov) => (
                            <div
                              key={prov.name}
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-all duration-200 ${
                                hover && activeIntegrations.has(intg.key)
                                  ? 'bg-indigo-500/10'
                                  : hover && !lit
                                    ? ''
                                    : ''
                              }`}
                            >
                              <span className={`flex-shrink-0 transition-colors duration-200 ${
                                hover && activeIntegrations.has(intg.key)
                                  ? prov.ready ? 'text-emerald-400' : 'text-amber-400'
                                  : hover && !lit
                                    ? 'text-slate-700'
                                    : prov.ready ? 'text-emerald-400' : 'text-slate-500'
                              }`}>
                                {providerIcons[prov.name]}
                              </span>
                              <span className={`transition-colors duration-200 ${
                                hover && activeIntegrations.has(intg.key)
                                  ? prov.ready ? 'text-white' : 'text-slate-300'
                                  : hover && !lit
                                    ? 'text-slate-700'
                                    : prov.ready ? 'text-slate-300' : 'text-slate-500'
                              }`}>
                                {prov.name}
                              </span>
                            </div>
                          ))}
                          <span className={`text-xs italic self-center transition-colors duration-200 px-2 py-1 ${
                            hover && activeIntegrations.has(intg.key) ? 'text-slate-400' : hover && !lit ? 'text-slate-700' : 'text-slate-500'
                          }`}>
                            + more integrations coming soon
                          </span>
                          </>
                        ) : (
                          <span className={`text-xs italic transition-colors duration-200 px-2 py-1 ${
                            hover && activeIntegrations.has(intg.key) ? 'text-slate-400' : hover && !lit ? 'text-slate-700' : 'text-slate-500'
                          }`}>
                            More integrations coming soon
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mobile: Card-based fallback */}
          <div className="md:hidden space-y-6">
            <div className="grid grid-cols-2 gap-3">
              {serviceTypes.map((st) => (
                <Card key={st.key} variant="default" className="p-4">
                  <FeatureIcon gradient={st.gradient} className="mb-2">
                    <span className={st.iconColor}>{st.icon}</span>
                  </FeatureIcon>
                  <h3 className="text-xs font-semibold text-slate-100 tracking-tight mb-2">{st.name}</h3>
                  <div className="space-y-1">
                    {integrations.map((intg) => {
                      const available = st.integrationKeys.includes(intg.key);
                      if (!available) return null;
                      return (
                        <div key={intg.key} className={`flex items-center gap-1.5 text-xs ${intg.comingSoon ? 'text-slate-500' : 'text-slate-300'}`}>
                          <Check className={`w-3 h-3 flex-shrink-0 ${intg.comingSoon ? 'text-amber-400/50' : 'text-emerald-400'}`} />
                          {intg.name}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {integrations.map((intg) => (
                <Card key={intg.key} variant="default" className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={intg.comingSoon ? 'text-slate-500' : 'text-slate-300'}>{intg.icon}</span>
                      <h3 className="text-sm font-semibold text-slate-100 tracking-tight">{intg.name}</h3>
                    </div>
                    <Badge variant={intg.comingSoon ? 'warning' : 'success'} size="sm">
                      {intg.comingSoon ? 'Soon' : 'Live'}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    {intg.providers.length > 0 ? (
                      <>
                        {intg.providers.map((prov) => (
                          <div key={prov.name} className="flex items-center gap-2 text-xs">
                            <span className={`flex-shrink-0 ${prov.ready ? 'text-emerald-400' : 'text-slate-500'}`}>
                              {providerIcons[prov.name]}
                            </span>
                            <span className={prov.ready ? 'text-slate-300' : 'text-slate-500'}>{prov.name}</span>
                          </div>
                        ))}
                        <p className="text-xs text-slate-500 italic">+ more integrations coming soon</p>
                      </>
                    ) : (
                      <p className="text-xs text-slate-500 italic">More integrations coming soon</p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>

        <SectionDivider variant="gradient" />

        {/* Features Grid */}
        <div className="my-12 md:my-16">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3">
              No Buttons. No Forms. Just Conversations.
            </h2>
            <p className="text-sm text-slate-400 max-w-xl mx-auto leading-relaxed">
              Customers talk. AI handles it. You grow.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-12 md:mb-16">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} variant="elevated" hoverable className="group p-6">
                  <div className="flex items-start justify-between mb-4">
                    <FeatureIcon gradient={feature.gradient}>
                      <Icon className={`w-6 h-6 ${feature.iconColor}`} />
                    </FeatureIcon>
                    <Badge variant={feature.comingSoon ? 'warning' : 'success'} size="sm">
                      {feature.benefit}
                    </Badge>
                  </div>
                  <h3 className="text-lg sm:text-xl text-slate-200 font-semibold tracking-tight mb-2 group-hover:text-white transition-colors">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">{feature.description}</p>
                </Card>
              );
            })}
          </div>

          {/* Security */}
          <Card variant="highlighted" className="group p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
              <FeatureIcon gradient="from-rose-500/20 to-pink-500/20" size="lg" className="flex-shrink-0">
                <Shield className="w-8 h-8 text-rose-400" />
              </FeatureIcon>
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="text-lg sm:text-xl text-slate-200 font-semibold tracking-tight">Your Data. Your Rules.</h3>
                  <Badge variant="premium">All Plans</Badge>
                </div>
                <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
                  GDPR compliant. Encrypted at rest and in transit. Need it inside your own network? Our own stack, Fnk, also ships as a private deployment.
                </p>
              </div>
            </div>
          </Card>
        </div>

        <SectionDivider variant="gradient" />

        {/* Built on the Fnk AI stack */}
        <div className="my-16 md:my-20">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3 text-center">
            Built on the Fnk AI Stack
          </h2>
          <p className="text-sm sm:text-base text-slate-400 text-center max-w-2xl mx-auto mb-10 leading-relaxed">
            DialogMe runs on its own AI stack,{' '}
            <a href="https://fnk.ai" target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 underline decoration-indigo-500/40 underline-offset-2">Fnk</a>
            , and operates the inference itself rather than reselling a model vendor&apos;s API. That is
            what lets us price per conversation instead of per token.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            <Card variant="default" hoverable className="p-5">
              <FeatureIcon gradient="from-indigo-500/20 to-purple-500/20" className="mb-3">
                <Cpu className="w-5 h-5 text-indigo-400" />
              </FeatureIcon>
              <h3 className="text-sm font-semibold text-slate-100 tracking-tight mb-1.5">No vendor lock-in</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Your service is not tied to one model provider. Switching is a configuration change,
                not a migration.
              </p>
            </Card>
            <Card variant="default" hoverable className="p-5">
              <FeatureIcon gradient="from-emerald-500/20 to-teal-500/20" className="mb-3">
                <Zap className="w-5 h-5 text-emerald-400" />
              </FeatureIcon>
              <h3 className="text-sm font-semibold text-slate-100 tracking-tight mb-1.5">Priced per conversation</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                You pay for conversations handled, not for tokens you cannot forecast — and the
                margin holds as your volume grows.
              </p>
            </Card>
            <Card variant="default" hoverable className="p-5">
              <FeatureIcon gradient="from-cyan-500/20 to-blue-500/20" className="mb-3">
                <Shield className="w-5 h-5 text-cyan-400" />
              </FeatureIcon>
              <h3 className="text-sm font-semibold text-slate-100 tracking-tight mb-1.5">Runs where you need it</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Because the stack is owned, not rented, the same product can run managed by us or
                entirely inside your network as a Fnk deployment.
              </p>
            </Card>
          </div>
        </div>

        <SectionDivider variant="gradient" />

        {/* Private deployment, by Fnk */}
        <div className="my-16 md:my-20">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3 text-center">
            Private Deployment, by Fnk
          </h2>
          <p className="text-sm sm:text-base text-slate-400 text-center max-w-2xl mx-auto mb-10 leading-relaxed">
            For teams whose data cannot leave the building — health, legal, financial, public sector,
            or anyone bound by a client contract. DialogMe itself is a managed platform; for a private
            deployment, our own stack ships as Fnk.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8">
            {[
              { title: 'Managed', detail: 'DialogMe runs it. Fastest to start.' },
              { title: 'Private cloud', detail: 'Your tenancy, your controls. Via Fnk.' },
              { title: 'On-premises', detail: 'Your data centre, your network. Via Fnk.' },
              { title: 'Air-gapped', detail: 'No outbound connectivity at all. Via Fnk.' },
            ].map((d) => (
              <Card key={d.title} variant="elevated" className="p-5 text-center">
                <h3 className="text-sm font-semibold text-slate-100 tracking-tight mb-1.5">{d.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{d.detail}</p>
              </Card>
            ))}
          </div>
          <Card variant="highlighted" className="p-5 sm:p-6">
            <p className="text-sm text-slate-300 leading-relaxed text-center">
              <span className="text-indigo-300 font-semibold">Permission-aware from the start.</span>{' '}
              Retrieval is filtered by each user&apos;s real entitlements in the source system — nobody
              gets an answer from a document they could not open themselves.
            </p>
          </Card>
          <div className="text-center mt-8">
            <Link to="/enterprise">
              <Button variant="outline" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Explore Enterprise
              </Button>
            </Link>
          </div>
        </div>

        {/* CTA */}
        <Card variant="cta" className="text-center p-6 sm:p-8 md:p-10">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-4">
            Seeing Is Believing
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto leading-relaxed">
            No credit card. No setup. Just conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo">
              <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Try Live Demo
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                View Pricing
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </main>
  );
}
